import { useMemo, useState } from 'react';
import { CornerDownRight, GripVertical, Plus, Columns2, X } from 'lucide-react';
import { SearchableCombobox, type ComboboxOption } from '@/components/ui/SearchableCombobox';
import { cn } from '@/lib/utils';
import { DIMENSION_GROUPS, DIMENSIONS, type DimensionKey } from './flexCatalog';
import type { FlexView } from './pivot';

/**
 * Pick up to two dimensions (R9–R11).
 *
 * The swap is native drag on the handle, plus ArrowUp/ArrowDown on the same
 * handle for keyboard users. The house list hook (useStepDragReorder) decides
 * the drop side from the vertical half of the hovered card, which does not
 * suit two chips laid side by side, and a two-item swap needs no insertion
 * slots — so this keeps its own few lines of native drag and adds no library.
 */

export interface DimensionBuilderProps {
  primary: DimensionKey | null;
  secondary: DimensionKey | null;
  view: FlexView;
  onChange: (next: { primary: DimensionKey | null; secondary: DimensionKey | null }) => void;
  onViewChange: (view: FlexView) => void;
}

type Slot = 'primary' | 'secondary';

const DRAG_TYPE = 'text/plain';
const DRAG_PREFIX = 'flex-dimension:';

function optionsExcluding(other: DimensionKey | null): ComboboxOption<DimensionKey>[] {
  return DIMENSION_GROUPS.flatMap((group) =>
    group.dimensions
      .filter((d) => d !== other)
      .map((d) => ({ value: d, label: DIMENSIONS[d].label, group: group.label })),
  );
}

export function DimensionBuilder({ primary, secondary, view, onChange, onViewChange }: DimensionBuilderProps) {
  // A second slot opened with "+" but not yet given a value.
  const [addingSecondary, setAddingSecondary] = useState(false);
  const [dragging, setDragging] = useState<Slot | null>(null);
  const [dropTarget, setDropTarget] = useState<Slot | null>(null);

  const primaryOptions = useMemo(() => optionsExcluding(secondary), [secondary]);
  const secondaryOptions = useMemo(() => optionsExcluding(primary), [primary]);

  const both = primary !== null && secondary !== null;
  const swap = () => {
    if (both) onChange({ primary: secondary, secondary: primary });
  };

  const setSlot = (slot: Slot, value: DimensionKey | null) => {
    if (slot === 'primary') {
      // Clearing the primary promotes the secondary rather than leaving a gap.
      if (value === null) onChange({ primary: secondary, secondary: null });
      else onChange({ primary: value, secondary });
    } else {
      setAddingSecondary(false);
      onChange({ primary, secondary: value });
    }
  };

  const endDrag = () => {
    setDragging(null);
    setDropTarget(null);
  };

  const renderSlot = (slot: Slot, value: DimensionKey | null) => {
    const label = value ? DIMENSIONS[value].label : null;
    const other: Slot = slot === 'primary' ? 'secondary' : 'primary';
    const acceptsDrop = both && dragging === other;
    return (
      <div
        role="group"
        aria-label={slot === 'primary' ? 'First dimension' : 'Second dimension'}
        className={cn(
          'flex items-center gap-1 rounded-md border border-transparent',
          dropTarget === slot && acceptsDrop && 'border-blue-400 bg-blue-50',
        )}
        onDragOver={(e) => {
          if (!acceptsDrop) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setDropTarget(slot);
        }}
        onDragLeave={() => setDropTarget((t) => (t === slot ? null : t))}
        onDrop={(e) => {
          if (!acceptsDrop) return;
          e.preventDefault();
          endDrag();
          swap();
        }}
      >
        {both && label && (
          <button
            type="button"
            draggable
            aria-label={`Move ${label}`}
            title={`Move ${label}`}
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData(DRAG_TYPE, `${DRAG_PREFIX}${slot}`);
              setDragging(slot);
            }}
            onDragEnd={endDrag}
            onKeyDown={(e) => {
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault();
                swap();
              }
            }}
            className="cursor-grab rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}
        <div className="w-52">
          <SearchableCombobox<DimensionKey>
            id={`flex-dimension-${slot}`}
            options={slot === 'primary' ? primaryOptions : secondaryOptions}
            value={value}
            onChange={(v) => setSlot(slot, v)}
            placeholder={slot === 'primary' ? 'Group by…' : 'Then by…'}
            emptyMessage="No matches"
          />
        </div>
        {(label || slot === 'secondary') && (
          <button
            type="button"
            aria-label={label ? `Remove ${label}` : 'Remove second dimension'}
            title={label ? `Remove ${label}` : 'Remove second dimension'}
            onClick={() => (slot === 'secondary' && !value ? setAddingSecondary(false) : setSlot(slot, null))}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  };

  const showSecondary = secondary !== null || (addingSecondary && primary !== null);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {renderSlot('primary', primary)}
      {showSecondary && renderSlot('secondary', secondary)}
      {primary !== null && !showSecondary && (
        <button
          type="button"
          aria-label="Add a second dimension"
          title="Add a second dimension"
          onClick={() => setAddingSecondary(true)}
          className="rounded-md border border-dashed border-slate-300 p-1.5 text-slate-500 hover:border-slate-400 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Plus className="h-4 w-4" />
        </button>
      )}
      {both && (
        // FilterTabs' segmented style; FilterTabs itself takes text labels only.
        <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1">
          {(
            [
              { value: 'nested', label: 'Nested view', icon: <CornerDownRight className="h-4 w-4" /> },
              { value: 'side-by-side', label: 'Side-by-side view', icon: <Columns2 className="h-4 w-4" /> },
            ] as const
          ).map((option) => (
            <button
              key={option.value}
              type="button"
              aria-label={option.label}
              title={option.label}
              aria-pressed={view === option.value}
              onClick={() => onViewChange(option.value)}
              className={cn(
                'rounded-md px-2 py-1 text-sm font-medium transition-colors',
                view === option.value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900',
              )}
            >
              {option.icon}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
